import { Component, HostBinding, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Store } from "@ngrx/store";
import moment from "moment";
import { Subject, merge } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  startWith,
  takeUntil,
} from "rxjs/operators";
import {
  ChangeAccountAction,
  FetchAvailableAccountsAction,
} from "../accounts/accounts.actions";
import { AvailableAccount } from "../accounts/accounts.interface";
import {
  getAvailableAccountsSelector,
  getCurrentAccount,
} from "../accounts/accounts.reducer";
import { ToastrTranslatedService } from "../shared/toastr-translated.service";
import { AuditLogDetailsDialogComponent } from "./audit-log-details-dialog/audit-log-details-dialog.component";
import { AuditLogEntry, AuditLogQuery } from "./audit-log.interface";
import { AuditLogService } from "./audit-log.service";

@Component({
  selector: "app-audit-log",
  templateUrl: "./audit-log.component.html",
  styleUrls: ["./audit-log.component.scss"],
  standalone: false,
})
export class AuditLogComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    "createdAt",
    "email",
    "actionType",
    "entityType",
    "entityName",
    "summary",
    "details",
  ];
  readonly dataSource = new MatTableDataSource<AuditLogEntry>([]);
  pageSize = 50;
  readonly pageSizeOptions = [25, 50, 100];
  selectedDomain = "";
  availableDomains: AvailableAccount[] = [];
  readonly identityLabel = (value: string) => value;
  readonly domainLabel = (account: AvailableAccount) => account.domain;
  readonly domainValue = (account: AvailableAccount) => account.domain;
  sortBy: AuditLogQuery["sortBy"] = "createdAt";
  sortDirection: "asc" | "desc" = "desc";
  total = 0;
  loading = true;
  private readonly destroy$ = new Subject<void>();

  @HostBinding("class.is-empty")
  get isEmpty(): boolean {
    return !this.loading && this.total === 0 && !this.isFilterActive;
  }

  private get searchPhrase(): string {
    const value = this.filters.get("search")?.value;
    return (value ?? "").trim();
  }

  get isFilterActive(): boolean {
    const { actionType, entityType } = this.filters.getRawValue();
    return Boolean(this.searchPhrase || actionType || entityType);
  }

  get showNoSearchResults(): boolean {
    return !this.loading && this.isFilterActive && this.total === 0;
  }

  readonly actionTypes = [
    "TEMPLATE_CREATED",
    "TEMPLATE_UPDATED",
    "TEMPLATE_DELETED",
    "DOMAIN_RULES_UPDATED",
    "DOMAIN_RULES_INSTALL_TRIGGERED",
    "SETTINGS_UPDATED",
    "INTEGRATION_SETTINGS_UPDATED",
    "EMPLOYEE_CREATED",
    "EMPLOYEE_UPDATED",
    "EMPLOYEE_DELETED",
    "EMPLOYEES_EXCLUDE_BULK_UPDATED",
    "EMPLOYEE_INVITE_SENT",
    "SIGNATURE_REMOVED",
    "SIGNATURES_EXPORTED",
    "EMPLOYEE_SYNC_TRIGGERED",
    "EMPLOYEE_IMPORT_SYNCED",
    "USER_INVITED",
    "USER_ROLE_UPDATED",
    "USER_REMOVED",
    "API_TOKEN_CREATED",
    "API_TOKEN_REVOKED",
    "API_TOKEN_EXTENDED",
    "BILLING_SETTINGS_UPDATED",
    "PENDING_PLAN_CHANGE_SCHEDULED",
    "PENDING_PLAN_CHANGE_CANCELLED",
    "PAYMENT_METHOD_UPDATED",
    "BRANCH_CREATED",
    "BRANCH_UPDATED",
    "BRANCH_DELETED",
    "MESSAGE_CREATED",
    "MESSAGE_UPDATED",
    "MESSAGE_DELETED",
    "MESSAGE_REORDERED",
    "MESSAGE_INSTALL_TRIGGERED",
    "MESSAGE_REINSTALL_TRIGGERED",
    "MESSAGE_REMOVED_FROM_EMPLOYEE",
    "COMPANY_PROFILE_UPDATED",
    "COMPANY_LOGO_UPDATED",
    "DOMAIN_ADDED",
    "DOMAIN_UPDATED",
    "SUBDOMAIN_ADDED",
    "GROUP_DEFAULT_SIGNATURE_UPDATED",
    "SCHEDULE_REINSTALL_UPDATED",
    "SCHEDULE_CREATED",
    "SCHEDULE_DELETED",
    "RESELLER_CLIENT_ATTACHED",
    "RESELLER_CLIENT_DETACHED",
    "MICROSOFT_TENANT_UPDATED",
    "TENANT_CREATED",
    "TENANT_UPDATED",
    "TENANT_DELETED",
    "TENANT_LEGACY_BILLING_UPDATED",
    "TENANT_UPFRONT_PAYMENT_UPDATED",
    "ADDON_CREATED",
    "ADDON_UPDATED",
    "ADDON_DELETED",
    "TEMPLATE_DUPLICATED",
    "TEMPLATE_REORDERED",
    "TEMPLATE_DEFAULT_FOR_NEW_EMPLOYEE_UPDATED",
    "TEMPLATE_REINSTALL_TRIGGERED",
    "DIRECTORY_SYNC_TRIGGERED",
    "DIRECTORY_SYNC_STOPPED",
    "SIGNATURE_VARIABLES_UPDATED",
    "SIGNATURE_INSTALL_TRIGGERED",
    "CHECKOUT_SESSION_CREATED",
    "FAILED_PAYMENT_CHECKOUT_SESSION_CREATED",
    "ADDITIONAL_LICENSES_PURCHASE_INITIATED",
    "UPFRONT_CHECKOUT_SESSION_CREATED",
    "UPFRONT_LICENSE_INCREASE_CHECKOUT_SESSION_CREATED",
    "UPFRONT_PENDING_LICENSE_CHANGE_SET",
    "UPFRONT_SUBSCRIPTION_CHANGES_SCHEDULED",
    "UPFRONT_PENDING_LICENSE_CHANGE_CANCELLED",
    "BILLING_PORTAL_SESSION_CREATED",
    "PLAN_CHECKOUT_TRIGGERED",
    "NFC_WRITE_PASSWORD_VIEWED",
    "NFC_MANUAL_ENCODE",
    "NFC_PASSWORD_REMOVED",
  ];
  readonly entityTypes = [
    "TEMPLATE",
    "TEMPLATE_DOMAIN_RULES",
    "SETTINGS",
    "INTEGRATION",
    "EMPLOYEE",
    "EMPLOYEE_BULK",
    "USER_ROLE",
    "SIGNATURE",
    "EMPLOYEE_IMPORT",
    "API_TOKEN",
    "BILLING_SETTINGS",
    "BRANCH",
    "MESSAGE",
    "COMPANY",
    "DOMAIN",
    "ORGANISATION_GROUP",
    "SCHEDULE",
    "RESELLER_ASSIGNMENT",
    "TENANT",
    "ADDON",
    "INTEGRATION_SYNC",
    "SIGNATURE_VARIABLE",
    "PRINT_CARD_ORDER_ITEM",
    "NFC_TAG",
  ];

  readonly filters = this.formBuilder.group({
    search: this.formBuilder.nonNullable.control(""),
    actionType: this.formBuilder.control<string | null>(null),
    entityType: this.formBuilder.control<string | null>(null),
  });
  readonly dateRangeForm = new FormGroup({
    from: new FormControl(moment().startOf("month").toDate()),
    to: new FormControl(moment().toDate()),
  });

  constructor(
    private readonly auditLogService: AuditLogService,
    private readonly formBuilder: FormBuilder,
    private readonly toastr: ToastrTranslatedService,
    private readonly dialog: MatDialog,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new FetchAvailableAccountsAction());
    this.store
      .select(getAvailableAccountsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        this.availableDomains = accounts ?? [];
      });
    this.store
      .select(getCurrentAccount)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((domain) => {
        if (!domain) {
          return;
        }
        this.selectedDomain = domain;
        this.fetch(1);
      });

    merge(this.filters.valueChanges, this.dateRangeForm.valueChanges)
      .pipe(startWith(null), debounceTime(350), takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetch(1);
      });
  }

  fetch = (page = 1): void => {
    this.loading = true;
    this.auditLogService
      .getLogs(this.buildQuery(page))
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.data;
          this.total = response.total;
        },
        error: () => this.toastr.error("auditLog.loadError"),
      });
  };

  onPage = (event: { pageIndex: number; pageSize: number }): void => {
    this.pageSize = event.pageSize;
    this.fetch(event.pageIndex + 1);
  };

  onSortChange = (event: Sort): void => {
    if (!event.direction) {
      this.sortBy = "createdAt";
      this.sortDirection = "desc";
      this.fetch(1);
      return;
    }
    this.sortBy = event.active as AuditLogQuery["sortBy"];
    this.sortDirection = event.direction;
    this.fetch(1);
  };

  export = (format: "csv" | "json"): void => {
    this.auditLogService.exportLogs(this.buildQuery(1), format).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `audit-log-${new Date().toISOString()}.${format}`;
        anchor.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.toastr.error("auditLog.exportError"),
    });
  };

  openDetails(entry: AuditLogEntry): void {
    this.dialog.open(AuditLogDetailsDialogComponent, {
      data: entry,
      width: "900px",
      maxWidth: "95vw",
      autoFocus: false,
      panelClass: "app-dialog-panel",
    });
  }

  onDomainChange(domain: string): void {
    if (!domain || domain === this.selectedDomain) {
      return;
    }
    this.store.dispatch(new ChangeAccountAction(domain));
  }

  private buildQuery(page: number): AuditLogQuery {
    const { actionType, entityType, search } = this.filters.value;
    const { from, to } = this.dateRangeForm.getRawValue();
    return {
      page,
      limit: this.pageSize,
      actionType: actionType || undefined,
      entityType: entityType || undefined,
      search: search || undefined,
      dateFrom: from
        ? moment(from).startOf("day").toDate().toISOString()
        : undefined,
      dateTo: to ? moment(to).endOf("day").toDate().toISOString() : undefined,
      sortBy: this.sortBy,
      order: this.sortDirection.toUpperCase(),
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
